import React, { useEffect, useState } from "react";
import { FileText, Calendar, Clock, CheckSquare, MessageSquare } from "lucide-react";
import { useRenewalCreate } from "./useRenewalCreate";
import StepData from "./StepData";
import StepDataMarketing from "./StepDataMarketing";
import StepTemplate from "./StepTemplate";
import StepPreview from "./StepPreview";
import StepInfoBasic from "./StepInfoBasic";
import StepTemplateMessage from "./StepTemplateMessage";
import PendingApprovalModal from "./PendingApprovalModal";
import { DynamicConfigStep } from "@/features/dynamic-campaigns/components/DynamicConfigStep";
import { PlanStatusAlert } from "../PlanStatusAlert";
import { usePlanStatus } from "@/hooks/usePlanStatus";
import CalendarCampaignStep from "../calendar/CalendarCampaignStep";

type PreCachedAssets = {
  sheets: any[];
  templates: any[];
  metaTemplates: any[];
} | null;

type Props = {
  onCancel?: () => void;
  onCreated?: (id: string) => void;
  allowTemplateCreation?: boolean;
  editingCampaign?: any;
  preCachedAssets?: PreCachedAssets;
};

const RenewalCreate: React.FC<Props> = ({ onCancel, onCreated, allowTemplateCreation = true, editingCampaign, preCachedAssets }) => {
  const hookData = useRenewalCreate(onCancel, onCreated, editingCampaign, preCachedAssets);
  const { isBlocked, getBlockedMessage } = usePlanStatus();
  const [calendarInternalStep, setCalendarInternalStep] = useState(-1);

  // resolver nombre de la hoja seleccionada (fallback al id)
  const selectedGoogleSheetName = (() => {
    try {
      const sheets = (hookData as any).sheets as any[] | undefined;
      if (!sheets || !hookData.googleSheetId) return undefined;
      const found = sheets.find(s =>
        String(s.sheet_id ?? s.id ?? s._id ?? "") === String(hookData.googleSheetId)
      );
      return found?.sheet_name ?? found?.name ?? undefined;
    } catch {
      return undefined;
    }
  })();

  const StepIndicator = () => {
    const isCalendar = (hookData as any).campaignType === "Calendar";
    
    const isInfo = hookData.step === "info";
    const isData = isCalendar 
      ? (calendarInternalStep === -1 || calendarInternalStep === 0 || calendarInternalStep === 1) && hookData.step === "calendar"
      : hookData.step === "data" || hookData.step === "dynamic_config";
    const isTemplate = isCalendar 
      ? calendarInternalStep === 2 && hookData.step === "calendar"
      : hookData.step === "template";
    const isMessage = isCalendar 
      ? calendarInternalStep === 3 && hookData.step === "calendar"
      : hookData.step === "message";
    const isPreview = isCalendar 
      ? calendarInternalStep === 4 && hookData.step === "calendar"
      : hookData.step === "preview";

    return (
      <div className="mt-6 w-full overflow-x-auto">
        <div className="flex w-max min-w-full flex-nowrap items-center justify-start lg:justify-center gap-6 px-2">
        {/* Paso 1: Información básica */}
        <div className={`flex items-center gap-3 ${isInfo ? "" : "opacity-60"}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isInfo ? "bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white" : "bg-white border border-gray-200"}`}>
            <FileText className={`w-5 h-5 ${isInfo ? "text-white" : "text-gray-400"}`} />
          </div>
          <div className="text-sm">
            <div className="font-semibold text-gray-900">Información básica</div>
            <div className="text-xs text-gray-400">Nombre y tipo</div>
          </div>
        </div>
        <div className="h-px w-10 bg-gray-200" />

        {/* Paso 2: Datos */}
        <div className={`flex items-center gap-3 ${isData ? "" : "opacity-60"}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isData ? "bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white" : "bg-white border border-gray-200"}`}>
            <Calendar className={`w-5 h-5 ${isData ? "text-white" : "text-gray-400"}`} />
          </div>
          <div className="text-sm">
            <div className="font-semibold text-gray-900">Datos</div>
            <div className="text-xs text-gray-400">Configuración</div>
          </div>
        </div>
        <div className="h-px w-10 bg-gray-200" />

        {/* Paso 3: Plantilla */}
        <div className={`flex items-center gap-3 ${isTemplate ? "" : "opacity-60"}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isTemplate ? "bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white" : "bg-white border border-gray-200"}`}>
            <Clock className={`w-5 h-5 ${isTemplate ? "text-white" : "text-gray-400"}`} />
          </div>
          <div className="text-sm">
            <div className="font-semibold text-gray-900">Plantilla</div>
            <div className="text-xs text-gray-400">De mensaje</div>
          </div>
        </div>
        <div className="h-px w-10 bg-gray-200" />

        {/* Paso 4: Mensaje */}
        <div className={`flex items-center gap-3 ${isMessage ? "" : "opacity-60"}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isMessage ? "bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white" : "bg-white border border-gray-200"}`}>
            <MessageSquare className={`w-5 h-5 ${isMessage ? "text-white" : "text-gray-400"}`} />
          </div>
          <div className="text-sm">
            <div className="font-semibold text-gray-900">Mensaje</div>
            <div className="text-xs text-gray-400">Edición</div>
          </div>
        </div>
        <div className="h-px w-10 bg-gray-200" />

        {/* Paso 5: Vista Previa */}
        <div className={`flex items-center gap-3 ${isPreview ? "" : "opacity-60"}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isPreview ? "bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white" : "bg-white border border-gray-200"}`}>
            <CheckSquare className={`w-5 h-5 ${isPreview ? "text-white" : "text-gray-400"}`} />
          </div>
          <div className="text-sm">
            <div className="font-semibold text-gray-900">Confirmar</div>
            <div className="text-xs text-gray-400">Resumen final</div>
          </div>
        </div>
      </div>
    </div>
    );
  };

  // Push /renewal-builder on mount and restore /renewals on unmount
  useEffect(() => {
    try {
      window.history.pushState({}, "", "/renewal-builder");
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch (e) {
      console.debug("No se pudo cambiar la URL a /renewal-builder en mount:", e);
    }
    return () => {
      try {
        window.history.replaceState({}, "", "/renewals");
        window.dispatchEvent(new PopStateEvent('popstate'));
      } catch (e) {
        console.debug("No se pudo restaurar la URL a /renewals en unmount:", e);
      }
    };
  }, []);

  // Handle OAuth callback from Google Sheets
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authStatus = params.get('auth');

    if (authStatus === 'success') {
      console.log('OAuth success detected, reloading page to refresh sheets');
      const url = new URL(window.location.href);
      url.searchParams.delete('auth');
      window.history.replaceState({}, '', url.toString());

      try {
        if (typeof window !== 'undefined' && (window as any).toast) {
          (window as any).toast.success('Conexión con Google Sheets completada exitosamente');
        }
      } catch (e) {
        console.log('Toast not available, showing alert');
        alert('Conexión con Google Sheets completada exitosamente');
      }

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else if (authStatus === 'retry') {
      const url = new URL(window.location.href);
      url.searchParams.delete('auth');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fadeIn">
      {((hookData as any).campaignType === "Calendar" && calendarInternalStep === 5) ? (
        /* Padding to maintain vertical centering when header is gone */
        <div className="h-4" />
      ) : (
        <div className="relative text-center">
          <h1 className="text-3xl font-bold text-gray-900">{editingCampaign ? 'Editar Campaña' : 'Crear Nueva Campaña'}</h1>
          <p className="text-gray-600 mt-1">Configura tu campaña paso a paso</p>
          <StepIndicator />
        </div>
      )}

      {/* Plan Status Alert - Solo mostrar si está bloqueado */}
      {isBlocked && (
        <div className="mb-6">
          <PlanStatusAlert showOnlyBlocked={true} />
          <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
            <p className="text-sm text-gray-600 mb-3">
              No puedes crear nuevas campañas mientras tu cuenta esté bloqueada.
            </p>
            <button
              onClick={() => hookData.handleCancel()}
              className="px-6 py-2.5 rounded-lg text-white font-medium shadow-md transition-all active:scale-95 bg-gradient-to-r from-[#1f4e66] to-[#1CB3B3] hover:opacity-90"
            >
              Volver atrás
            </button>
          </div>
        </div>
      )}

      {/* Solo mostrar los pasos si no está bloqueado */}
      {!isBlocked && (
        <>
          {hookData.step === "template" && (
            <StepTemplate
              templateId={hookData.templateId} setTemplateId={hookData.setTemplateId}
              templates={hookData.availableMetaTemplates}
              showTemplateModal={hookData.showTemplateModal} setShowTemplateModal={hookData.setShowTemplateModal}
              handleTemplateCreated={hookData.handleTemplateCreated}
              onBack={() => hookData.setStep(hookData.campaignType === "Dynamic" ? "dynamic_config" : hookData.campaignType === "Calendar" ? "info" : "data")} onNext={() => hookData.setStep("message")}
              // New props for persistence
              templateType={hookData.templateType} setTemplateType={hookData.setTemplateType}
              templateLanguage={hookData.templateLanguage} setTemplateLanguage={hookData.setTemplateLanguage}
              templateNameInput={hookData.templateNameInput} setTemplateNameInput={hookData.setTemplateNameInput}
              setMessageTemplate={hookData.setMessageTemplate}
              campaignName={hookData.name}
              isLoadingTemplates={hookData.loadingAssets}
              // Props for 24-hour validation (Marketing only)
              campaignType={hookData.campaignType}
              sendDate={hookData.sendDate}
              sendTime={hookData.sendingTime}
            />
          )}

          {hookData.step === "info" && (
            <StepInfoBasic
              name={hookData.name}
              setName={(v) => {
                hookData.setName(v);
                if (hookData.nameConflictError) hookData.setNameConflictError(null);
              }}
              campaignType={(hookData as any).campaignType}
              setCampaignType={(hookData as any).setCampaignType}
              onCancel={() => hookData.handleCancel()}
              onNext={() => (hookData as any).handleNextFromInfo()}
              nameConflictError={hookData.nameConflictError}
              checkingCampaignName={hookData.checkingCampaignName}
            />
          )}

          {/* ── Google Calendar campaign flow ────────────────────────────── */}
          {hookData.step === "calendar" && (
            <CalendarCampaignStep
              onCreated={() => {
                // Calendar campaigns are created inside CalendarCampaignStep;
                // once done, close the wizard and reload the campaign list.
                if (onCreated) onCreated("");
                else if (onCancel) onCancel();
              }}
              onCancel={() => hookData.handleCancel()}
              onStepChange={(step: number) => {
                setCalendarInternalStep(step);
              }}
              initialCampaignName={hookData.name}
            />
          )}

          {hookData.step === "dynamic_config" && (
            <DynamicConfigStep
              excludedTabNames={hookData.tabNamesInUseForSheet}
              excludedTabIds={hookData.tabIdsInUseForSheet}
              config={{
                google_sheet_id: hookData.googleSheetId,
                google_sheet_tab_id: hookData.googleSheetTabId,
                google_sheet_tab_name: hookData.googleSheetTabName,
                date_column: hookData.dynamicDateColumn,
                time_column: hookData.dynamicTimeColumn,
                phone_column: hookData.phoneColumn,
                is_appointment_mode: hookData.isAppointmentMode,
                use_default_reminders: hookData.useDefaultReminders,
                reminder_days_before: hookData.dynamicReminderDays,
                reminder_hours_before: hookData.dynamicReminderHours
              }}
              onConfigChange={(newConfig) => {
                const nextTabName = newConfig.google_sheet_tab_name || "";

                const sheetChanged =
                  newConfig.google_sheet_id !== hookData.googleSheetId;

                const tabChanged =
                  nextTabName !== hookData.googleSheetTabName;

                
                if (sheetChanged || tabChanged) {
                  hookData.setColumnMapping({});
                }

                if (sheetChanged && newConfig.google_sheet_id) {
                  void hookData.reloadSheets();
                }


                hookData.setGoogleSheetId(newConfig.google_sheet_id);
                hookData.setGoogleSheetTabId(newConfig.google_sheet_tab_id ?? null);
                hookData.setGoogleSheetTabName(nextTabName);
                hookData.setDynamicDateColumn(newConfig.date_column);
                hookData.setDynamicTimeColumn(newConfig.time_column || "");
                hookData.setPhoneColumn(newConfig.phone_column);
                hookData.setIsAppointmentMode(newConfig.is_appointment_mode || false);
                hookData.setUseDefaultReminders(newConfig.use_default_reminders || false);
                hookData.setDynamicReminderDays(newConfig.reminder_days_before || []);
                hookData.setDynamicReminderHours(newConfig.reminder_hours_before || []);
              }}
              onNext={hookData.handleNextFromDynamicConfig}
              onBack={() => hookData.setStep("info")}
              campaignName={hookData.name}
            />
          )}

          {hookData.step === "data" && hookData.campaignType === "Marketing" && (
            <StepDataMarketing
              campaignName={hookData.name}
              googleSheetId={hookData.googleSheetId}
              googleSheetTabName={hookData.googleSheetTabName}
              selectSheetTab={hookData.selectSheetTab}
              excludedTabNames={hookData.tabNamesInUseForSheet}
              excludedTabIds={hookData.tabIdsInUseForSheet}
              sheetColumns={hookData.sheetColumns}
              columnValidationStatus={hookData.columnValidationStatus}
              phoneColumn={hookData.phoneColumn}
              setPhoneColumn={hookData.setPhoneColumn}
              loadingAssets={hookData.loadingAssets}
              sheets={hookData.sheets}
              visibleSheets={hookData.visibleSheets}
              total={hookData.total}
              totalPages={hookData.totalPages}
              currentPage={hookData.currentPage}
              setCurrentPage={hookData.setCurrentPage}
              start={hookData.start}
              selectSheet={hookData.selectSheet}
              reloadColumns={hookData.reloadColumns}
              reloadSheets={hookData.reloadSheets}
              metaTemplates={hookData.availableMetaTemplates}
              loadingMetaTemplates={(hookData as any).loadingMetaTemplates}
              selectedMetaTemplate={hookData.selectedMetaTemplate}
              setSelectedMetaTemplate={hookData.setSelectedMetaTemplate}
              sendDate={hookData.sendDate}
              setSendDate={hookData.setSendDate}
              sendTime={hookData.sendingTime}
              setSendTime={hookData.setSendingTime}
              sendImmediately={hookData.sendImmediately}
              setSendImmediately={hookData.setSendImmediately}
              onBack={() => hookData.setStep("info")}
              onNext={hookData.handleNextFromDataMarketing}
            />
          )}

          {hookData.step === "data" && hookData.campaignType !== "Marketing" && (
            <StepData
              campaignName={hookData.name}
              googleSheetId={hookData.googleSheetId}
              googleSheetTabName={hookData.googleSheetTabName}
              selectSheetTab={hookData.selectSheetTab}
              excludedTabNames={hookData.tabNamesInUseForSheet}
              excludedTabIds={hookData.tabIdsInUseForSheet}
              sheetColumns={hookData.sheetColumns}
              columnValidationStatus={hookData.columnValidationStatus}
              phoneColumn={hookData.phoneColumn} setPhoneColumn={hookData.setPhoneColumn}
              renewalDateColumn={hookData.renewalDateColumn} setRenewalDateColumn={hookData.setRenewalDateColumn}
              periodicityUnit={hookData.periodicityUnit} setPeriodicityUnit={hookData.setPeriodicityUnit}
              periodicityValue={hookData.periodicityValue} setPeriodicityValue={hookData.setPeriodicityValue}
              onPeriodicityChange={hookData.handlePeriodicityChange}
              isCustomPeriodicity={hookData.isCustomPeriodicity}
              onEnableCustomPeriodicity={hookData.handleEnableCustomPeriodicity}
              onDisableCustomPeriodicity={hookData.handleDisableCustomPeriodicity}
              onCustomPeriodicityChange={hookData.handleCustomPeriodicityChange}
              reminderDays={hookData.reminderDays} reminderInput={hookData.reminderInput} setReminderInput={hookData.setReminderInput}
              addReminderFromInput={hookData.addReminderFromInput} removeReminder={hookData.removeReminder}
              sendingTime={hookData.sendingTime} setSendingTime={hookData.setSendingTime}
              loadingAssets={hookData.loadingAssets} sheets={hookData.sheets} visibleSheets={hookData.visibleSheets}
              total={hookData.total} totalPages={hookData.totalPages} currentPage={hookData.currentPage} setCurrentPage={hookData.setCurrentPage}
              start={hookData.start} selectSheet={hookData.selectSheet}
              reloadColumns={hookData.reloadColumns}
              reloadSheets={hookData.reloadSheets}
              showTutorialModal={hookData.showTutorialModal} setShowTutorialModal={hookData.setShowTutorialModal}
              onBack={() => hookData.setStep("info")} onNext={hookData.handleNextFromData}
            />
          )}
          {hookData.step === "message" && (
            <StepTemplateMessage
              messageTemplate={hookData.messageTemplate}
              setMessageTemplate={hookData.setMessageTemplate}
              sheetColumns={hookData.sheetColumns}
              sampleRow={hookData.sampleRow}
              phoneColumn={hookData.phoneColumn}
              templateId={hookData.templateId}
              templates={hookData.availableMetaTemplates}
              googleSheetId={hookData.googleSheetId}
              columnMapping={hookData.columnMapping}
              setColumnMapping={hookData.setColumnMapping}
              reloadColumns={hookData.reloadColumns}
              headerText={hookData.templateHeaderText}
              setHeaderText={hookData.setTemplateHeaderText}
              footerText={hookData.templateFooterText}
              setFooterText={hookData.setTemplateFooterText}
              buttons={hookData.templateButtons}
              setButtons={hookData.setTemplateButtons}
              parentHeaderType={hookData.templateHeaderType}
              setParentHeaderType={hookData.setTemplateHeaderType}
              parentHeaderImageHandle={hookData.templateHeaderImageHandle}
              setParentHeaderImageHandle={hookData.setTemplateHeaderImageHandle}
              setHeaderImageUrl={hookData.setTemplateHeaderImageUrl}
              parentHeaderImagePreview={hookData.templateHeaderImagePreview}
              setParentHeaderImagePreview={hookData.setTemplateHeaderImagePreview}
              parentHeaderEnabled={hookData.templateHeaderEnabled}
              setParentHeaderEnabled={hookData.setTemplateHeaderEnabled}
              parentFooterEnabled={hookData.templateFooterEnabled}
              setParentFooterEnabled={hookData.setTemplateFooterEnabled}
              parentButtonsEnabled={hookData.templateButtonsEnabled}
              setParentButtonsEnabled={hookData.setTemplateButtonsEnabled}
              campaignType={hookData.campaignType}
              onBack={() => hookData.setStep("template")}
              onNext={() => hookData.setStep("preview")}
            />
          )}
          {hookData.step === "preview" && (
            <StepPreview
              name={hookData.name}
              campaignType={hookData.campaignType}
              templateId={hookData.templateId}
              templateType={hookData.templateType}
              templateLanguage={hookData.templateLanguage}
              templateNameInput={hookData.templateNameInput}
              sampleRow={hookData.sampleRow}
              templateContent={hookData.messageTemplate || (() => {
                const t = hookData.templates.find((tpl: any) => String(tpl.id || tpl._id) === String(hookData.templateId));
                return t?.template || t?.message || undefined;
              })()}
              templateName={(() => {
                let t = hookData.templates.find((tpl: any) => String(tpl.id || tpl._id) === String(hookData.templateId));
                if (!t) {
                  t = hookData.metaTemplates.find((tpl: any) => String(tpl.id) === String(hookData.templateId));
                }
                return t?.displayName || t?.name || undefined;
              })()}
              headerText={hookData.templateHeaderText}
              headerType={hookData.templateHeaderType}
              headerImagePreview={hookData.templateHeaderImagePreview}
              footerText={hookData.templateFooterText}
              buttons={hookData.templateButtons}
              googleSheetId={hookData.googleSheetId}
              googleSheetName={selectedGoogleSheetName}
              googleSheetTabName={hookData.googleSheetTabName}
              columnMapping={hookData.columnMapping}
              phoneColumn={hookData.phoneColumn}
              renewalDateColumn={hookData.renewalDateColumn}
              dynamicDateColumn={hookData.dynamicDateColumn}
              dynamicTimeColumn={hookData.dynamicTimeColumn}
              periodicityUnit={hookData.periodicityUnit}
              periodicityValue={hookData.periodicityValue}
              reminderDays={hookData.reminderDays}
              sendingTime={hookData.sendingTime}
              sendDate={hookData.sendDate}
              saving={hookData.saving}
              isCreatingNewTemplate={!!(hookData.templateNameInput && hookData.templateNameInput.trim() !== "" && !hookData.templateId)}
              onBack={() => hookData.setStep("message")}
              onCreate={hookData.handleCreate}
            />
          )}

          {/* Modal de retroalimentación para plantillas pendientes de aprobación */}
          <PendingApprovalModal
            isOpen={hookData.showPendingApprovalModal}
            onClose={hookData.handleClosePendingApprovalModal}
            campaignName={hookData.pendingApprovalInfo?.campaignName || ""}
            templateName={hookData.pendingApprovalInfo?.templateName || ""}
            campaignType={hookData.pendingApprovalInfo?.campaignType || "Marketing"}
          />
        </>
      )}

    </div>
  );
};

export default RenewalCreate;